import { isCardElementEditable } from '../AccountCard/helpers';
import { hexToRGBA } from '../../../../util/hex-to-rgba';
import { CARD_ELEMENT_TYPE, PHOTO_TYPE } from '../../constants';
import { isElementCardBackground } from '../../../../components/global/CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';

/**
 * Determines whether the team-photo selection flow applies to a given
 * photo CardElement, mirroring the check in CardElementPhoto. When true,
 * the element should remain interactable even if not normally editable so
 * the user can switch between team photos.
 *
 * @param {Object} element The element UI state.
 * @param {Array} [teamPhotos] The team photos available for the card.
 * @returns {boolean}
 */
export const shouldUseTeamPhotosSelection = (element, teamPhotos = []) => {
  if (element?.cardElement?.type !== CARD_ELEMENT_TYPE.PHOTO) {
    return false;
  }

  const photoId = element?.cardElementConfiguration?.photo?.id;

  if (!photoId || !teamPhotos?.length) {
    return false;
  }

  return (
    teamPhotos.some(tp => tp?.photo?.id === photoId) &&
    !isCardElementEditable(element)
  );
};

/**
 * Determines whether the `alwaysAllowPhotoChange` override (e.g. admins,
 * PROs remaking an order) applies to a given element. Every slot showing
 * an actual photo is unlocked, no matter how it was configured
 * (auto-populated photos such as team photos are written as
 * non-editable, may come in as a plain photo URL and may carry any
 * `photo_type`). Only the Card's background and decorative elements not
 * showing a photo (asset-image overlays such as foreground art, logos or
 * card art) stay locked, as offering photo-change options on them makes
 * no sense and, being full-frame on top, they would swallow clicks meant
 * for the photos beneath.
 *
 * @param {Object} element The element UI state.
 * @returns {boolean}
 */
export const canAlwaysAllowPhotoChange = element => {
  const { cardElement, cardElementConfiguration } = element ?? {};

  if (cardElement?.type !== CARD_ELEMENT_TYPE.PHOTO) {
    return false;
  }

  if (isElementCardBackground(element)) {
    return false;
  }

  if (
    cardElementConfiguration?.photo?.id ||
    cardElementConfiguration?.photo_url
  ) {
    return true;
  }

  // Elements not showing an actual photo are either empty slots, which we
  // only unlock when they already are editable, or decorative asset-image
  // overlays, which stay locked.
  return isCardElementEditable(element);
};

/**
 * Converts a float into a percentage value for usage in CSS.
 *
 * @param {Number} float The float number.
 * @returns {string}
 */
const percentage = float => {
  if (!float) {
    return '0';
  }

  return `${float * 100}%`;
};

/**
 * Constructs the styles of a CardElement's frame.
 *
 * @param {Object} element The element UI state.
 * @param {Object} element.cardElement The CardElement.
 * @param {Object} element.cardElementConfiguration The CardElementConfiguration.
 * @param {Array} teamPhotos The team photos array provided by upper level components
 * @param {Boolean} alwaysAllowPhotoChange whether or not to ignore all the rest and always allow changing photos
 * @returns {Object}
 */
export const getCardElementFrameStyle = (
  element,
  teamPhotos = [],
  alwaysAllowPhotoChange = false,
) => {
  const { cardElement } = element;
  const isEditable = isCardElementEditable(element);
  // Allow click-through when the photo element is using team-photo selection,
  // even if not normally editable, so the user can switch between team photos.
  // Also allow it when `alwaysAllowPhotoChange` is set (e.g. admins) so a photo
  // slot can always be interacted with to change its photo. Decorative
  // elements (backdrops, foregrounds, logos, card art) stay locked.
  const isInteractable =
    isEditable ||
    shouldUseTeamPhotosSelection(element, teamPhotos) ||
    (alwaysAllowPhotoChange && canAlwaysAllowPhotoChange(element));

  const styles = {
    zIndex: cardElement.z_index,
    width: percentage(cardElement.frame.width),
    height: percentage(cardElement.frame.height),
    left: percentage(cardElement.frame.x),
    top: percentage(cardElement.frame.y),
    transform: `rotate(${cardElement.frame.rotation}rad)`,
    pointerEvents: isInteractable ? 'auto' : 'none',
  };

  return styles;
};

/**
 * Constructs the styles of a CardElement's frame container.
 *
 * @param {Object} element The element UI state.
 * @param {Object} element.cardElement The CardElement.
 * @param {Object} element.cardElementConfiguration The CardElementConfiguration.
 * @param {Object} [elementBounds] The CardSide's size (width/height).
 * @param {Number} [elementBounds.width] The Element's width in px.
 * @param {Number} [elementBounds.height] The Element's width in px.
 * @returns {Object}
 */
export const getCardElementFrameContainerStyle = (element, elementBounds) => {
  const { cardElement, cardElementConfiguration = {} } = element;
  const { border } = cardElementConfiguration ?? {};
  const cornerRadius = cardElementConfiguration?.corner_radius ?? 0;
  const minPixelDimension = Math.min(elementBounds.width, elementBounds.height);

  const styles = {
    borderRadius: `${Math.ceil((minPixelDimension * cornerRadius) / 2)}px`,
  };

  if (border?.width && elementBounds?.width) {
    styles.borderStyle = border.type.toLowerCase();
    styles.borderColor = hexToRGBA(border.color, border.opacity);

    if (border.width) {
      styles.boxSizing = 'border-box';
      // Border width is based on the width of the element
      // Dive by 2, since the max border is half of the minimum size
      styles.borderWidth = `${Math.ceil(
        (minPixelDimension * border.width) / 2,
      )}px`;
    }
  }

  // CardElementType specific styles

  if (cardElement.type === CARD_ELEMENT_TYPE.TEXT) {
    if (cardElementConfiguration.text_shadow) {
      const { text_shadow: shadow } = cardElementConfiguration;

      styles.textShadow = `${shadow.offset_x}px ${shadow.offset_y}px ${
        shadow.blur_radius
      }px ${hexToRGBA(shadow.color, shadow.opacity)}`;
    }

    if (typeof cardElementConfiguration.opacity !== 'undefined') {
      styles.opacity = cardElementConfiguration.opacity;
    }
  }

  if (cardElement.type === CARD_ELEMENT_TYPE.RECTANGLE) {
    if (cardElementConfiguration.color) {
      styles.color = cardElementConfiguration.color;
      styles.backgroundColor = cardElementConfiguration.color;
    }
  }

  if (cardElement.type === CARD_ELEMENT_TYPE.PHOTO) {
    if (cardElementConfiguration.background_color) {
      styles.backgroundColor = cardElementConfiguration.background_color;
    }

    if (
      cardElementConfiguration.asset_image?.url &&
      !cardElementConfiguration.cropbox
    ) {
      styles.backgroundImage = `url(${cardElementConfiguration.asset_image.url})`;
      styles.backgroundSize = 'cover';
      styles.backgroundRepeat = 'no-repeat';
      styles.backgroundPosition = 'center center';
    }

    if (cardElementConfiguration.photo_type === PHOTO_TYPE.LOGO) {
      styles.backgroundSize = 'contain';
    }

    if (cardElementConfiguration.box_shadow) {
      const { box_shadow: shadow } = cardElementConfiguration;

      styles.boxShadow = `${hexToRGBA(shadow.color, shadow.opacity)} ${
        shadow.offset_x
      }px ${shadow.offset_y}px ${shadow.blur_radius}px`;
    }

    if (typeof cardElementConfiguration.opacity !== 'undefined') {
      styles.opacity = cardElementConfiguration.opacity;
    }
  }

  return styles;
};
